import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    ExpenseSubMode,
    RevenueSubMode,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

const EXPENSE_ACCOUNT_TYPES: readonly AccountType[] = [
    AccountType.cogs,
    AccountType.operatingExpenses,
    AccountType.otherExpenses,
];

const INCOME_ACCOUNT_TYPES: readonly AccountType[] = [
    AccountType.income,
    AccountType.otherIncome,
];

export interface ScheduleTransactionMode {
    type: ScheduleType;
    revenueSubMode: RevenueSubMode | null;
    expenseSubMode: ExpenseSubMode | null;
}

/** Revenue schedules a payment received, expense one made. */
export function canScheduleTransactionAmount(options: {
    type: ScheduleType;
    amount: number;
}): boolean {
    switch (options.type) {
        case ScheduleType.revenue:
            return options.amount > 0;
        case ScheduleType.expense:
            return options.amount < 0;
        case ScheduleType.fixedAsset:
            return false;
    }
}

export function canScheduleTransactions(
    mode: ScheduleTransactionMode,
): boolean {
    switch (mode.type) {
        case ScheduleType.revenue:
            return mode.revenueSubMode !== null;
        case ScheduleType.expense:
            return mode.expenseSubMode !== null;
        case ScheduleType.fixedAsset:
            return false;
    }
}

/**
 * A revenue policy doesn't go on a transaction categorized to an expense
 * account, nor an expense policy on one categorized to an income account.
 * An uncategorized transaction fits either.
 */
export function canScheduleTransactionAccountType(options: {
    type: ScheduleType;
    accountType: AccountType | null;
}): boolean {
    const { type, accountType } = options;
    switch (type) {
        case ScheduleType.revenue:
            return !EXPENSE_ACCOUNT_TYPES.some(
                (expenseType) => expenseType === accountType,
            );
        case ScheduleType.expense:
            return !INCOME_ACCOUNT_TYPES.some(
                (incomeType) => incomeType === accountType,
            );
        case ScheduleType.fixedAsset:
            return false;
    }
}

export function canScheduleTransaction(options: {
    mode: ScheduleTransactionMode;
    amount: number;
    accountType: AccountType | null;
}): boolean {
    return (
        canScheduleTransactions(options.mode) &&
        canScheduleTransactionAmount({
            type: options.mode.type,
            amount: options.amount,
        }) &&
        canScheduleTransactionAccountType({
            type: options.mode.type,
            accountType: options.accountType,
        })
    );
}
